import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMedia } from '@/hooks';
import { hasTranslation } from '@/modules/localization';
import { replaceFsCount, useButtonAction } from '@/modules/promo';
import { BanksLogosList } from '@/modules/promo/components/BanksLogosList';
import { Button, Logo } from '../../components';
import {
  DashedCopyCode,
  type TCopyCodeButtonColor,
} from '../../components/DashedCopyCode';
import { FlyingElement } from '../../components/FlyingElements';
import { Loader } from '../../components/Loader/index';
import { TopGames } from '../../components/TopGames';
import type { FlyingElementSrc as FlyingElementType } from '../../types';
import { TermsAndConditions } from '../BenefitsTemplate/components/Terms&Conds';
import { ScWithoutBenefitsTemplate } from './styled';

export type TButtonColor = 'greenGradient' | 'secondaryGreenGradient';

type TButtonHeight = 'xxLarge' | 'xLarge';

interface IWithoutBenefitsTemplateProps {
  translation: string;
  bonusCode?: string;
  leftImage: string;
  mainImage: string;
  mobMainImage: string;
  bgImage: string;
  bgMobImg?: string;
  buttonCopyCodeColor?: TCopyCodeButtonColor;
  buttonColor?: TButtonColor;
  flyingElements?: FlyingElementType[];
  isImagesHidden?: boolean;
  validDate: string;
  leftRegButton?: boolean;
  buttonHeight?: TButtonHeight;
  leftRegBtnUnderTrms?: boolean;
  animatedButton?: boolean;
  shimmerButton?: boolean;
  topGames?: boolean;
  showSlider?: boolean;
  imgByLocation?: boolean;
  srcImgs?: string;
  bottomTopGames?: string[];
  shouldShowBanksList?: boolean;
  isReverseTermsArrow?: boolean;
  isStickyRegBtn?: boolean;
  fsCount?: number | null;
}

export const WithoutBenefitsTemplate = ({
  translation,
  bonusCode,
  leftImage,
  mainImage,
  mobMainImage,
  bgImage,
  bgMobImg,
  buttonCopyCodeColor,
  buttonColor = 'greenGradient',
  flyingElements,
  isImagesHidden,
  validDate,
  leftRegButton,
  buttonHeight = 'xxLarge',
  leftRegBtnUnderTrms,
  animatedButton,
  shimmerButton,
  topGames,
  showSlider,
  bottomTopGames,
  imgByLocation,
  srcImgs,
  isStickyRegBtn,
  isReverseTermsArrow,
  shouldShowBanksList,
  fsCount,
}: IWithoutBenefitsTemplateProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(translation);
  const [copyActive, setCopyActive] = useState(false);

  const { buttonAction, isLoading } = useButtonAction();
  const { isMobile } = useMedia();

  const buttonColorConfig = useMemo(
    () => ({
      greenGradient: {
        greenGradient: true,
      },
      secondaryGreenGradient: {
        secondaryGreenGradient: true,
      },
    }),
    []
  );

  const buttonHeightConfig = useMemo(
    () => ({
      xxLarge: {
        xxLarge: true,
      },
      xLarge: {
        xLarge: true,
      },
    }),
    []
  );

  const regButton = useMemo(
    () => (
      <div className={`button ${copyActive ? 'vibrating' : ''}`}>
        <Button
          animated={animatedButton}
          disabled={isLoading}
          shimmer={shimmerButton}
          wide
          {...buttonHeightConfig[buttonHeight]}
          {...buttonColorConfig[buttonColor]}
          {...buttonAction}
        >
          {isLoading ? <Loader /> : t('joinNow')}
        </Button>
      </div>
    ),
    [
      animatedButton,
      buttonAction,
      buttonColor,
      buttonColorConfig,
      buttonHeight,
      buttonHeightConfig,
      copyActive,
      isLoading,
      shimmerButton,
      t,
    ]
  );

  return (
    <ScWithoutBenefitsTemplate
      bgImage={bgImage}
      bgMobImg={bgMobImg}
      buttonHeight={buttonHeightConfig[buttonHeight]}
      copyActive={copyActive}
    >
      <img alt="left decor" className="left-decor-img" src={leftImage} />

      <div className="container">
        <div className="top-section">
          <div className="left-column">
            <Logo />
            <img alt="mobile main img" className="mob-img" src={mobMainImage} />
            <div className="bonus">
              <h2 className="title">
                <Trans i18nKey="bonus.title" ns={translation} />
              </h2>
              {hasTranslation(t, 'bonus.count') && (
                <p className="fs-count">
                  <Trans i18nKey="bonus.count" ns={translation} />
                </p>
              )}
              {hasTranslation(t, 'bonus.medium-count') && (
                <p
                  className={`fs-count medium-count ${language} ${fsCount && fsCount >= 100 ? 'three-digit' : ''}`}
                >
                  {replaceFsCount(
                    t('bonus.medium-count', { fs: fsCount }),
                    fsCount
                  )}
                </p>
              )}
              {hasTranslation(t, 'bonus.offer') && (
                <p className="fs">
                  <Trans i18nKey="bonus.offer" ns={translation} />
                </p>
              )}
              {hasTranslation(t, 'bonus.fs') && (
                <p className="fs">{t('bonus.fs')}</p>
              )}
              {hasTranslation(t, 'bonus.description') && (
                <p className="title">{t('bonus.description')}</p>
              )}
            </div>
            {bonusCode && (
              <div className="code-wrapper">
                <DashedCopyCode
                  buttonColor={buttonCopyCodeColor}
                  code={bonusCode}
                  copyActive={copyActive}
                  setCopyActive={setCopyActive}
                />
              </div>
            )}
            {leftRegButton && (
              <div className="left-reg-button">{regButton}</div>
            )}
            <TermsAndConditions
              isReverseArrow={isReverseTermsArrow}
              translation={translation}
              validDate={validDate}
            />
            {leftRegBtnUnderTrms && (
              <div className="left-reg-button">{regButton}</div>
            )}
          </div>
          <div className="right-column">
            {!isImagesHidden && (
              <FlyingElement
                flyingElements={flyingElements}
                maxWidthMobile={1015}
              />
            )}
            <img alt="main img" src={mainImage} />
            {!(
              leftRegButton ||
              leftRegBtnUnderTrms ||
              (isStickyRegBtn && isMobile)
            ) && regButton}
            {topGames && (
              <TopGames
                imgByLocation={imgByLocation}
                showSlider={showSlider}
                srcImgs={srcImgs}
              />
            )}
          </div>
        </div>
      </div>
      {bottomTopGames && (
        <div className="bottom-games">
          <TopGames
            centeredSlides
            customImages={bottomTopGames}
            fullMobWidth
            loopSlider={isMobile}
            mobImgSize={132}
            showSlider={isMobile}
            slidesPerView={4}
            spaceBetween={12}
          />
        </div>
      )}
      {shouldShowBanksList && (
        <div className="payments-wrapper">
          <BanksLogosList bgColor="blue" />
        </div>
      )}
      <div className="mobile-reg-btn">
        {!(leftRegButton && leftRegBtnUnderTrms) &&
          isStickyRegBtn &&
          isMobile &&
          regButton}
      </div>
    </ScWithoutBenefitsTemplate>
  );
};
